/**
 * Abrangencia dos planos (planos por orgao, 21/10/2026). Espelho das regras do backend
 * (5.3 shared/application/helpers/plan-scope.helpers.ts):
 * - NATIONAL: o plano de sempre, vale para licitacoes de qualquer orgao;
 * - AGENCY: vale so para as licitacoes de um orgao.
 * A assinatura guarda a abrangencia da compra (planScope/planAgencyId); sem ela e nacional.
 * Funcoes puras, testadas em tests/unit/domain/plan-scope.test.ts.
 */

export type PlanScope = 'NATIONAL' | 'AGENCY';

export const bareId = (value: unknown): string => {
    const s = String(value ?? '').trim();
    return s.includes(':') ? (s.split(':').pop() ?? '') : s;
};

export interface ScopedPlan {
    scope?: string | null;
    agencyId?: string | null;
    agencyName?: string | null;
}

export interface ScopedSubscription {
    status?: string | null;
    active?: boolean | null;
    endDate?: string | null;
    planScope?: string | null;
    planAgencyId?: string | null;
}

export const planScopeOf = (plan: ScopedPlan | null | undefined): PlanScope =>
    String(plan?.scope ?? '').toUpperCase() === 'AGENCY' && bareId(plan?.agencyId) ? 'AGENCY' : 'NATIONAL';

export const subscriptionScopeOf = (sub: ScopedSubscription | null | undefined): PlanScope =>
    String(sub?.planScope ?? '').toUpperCase() === 'AGENCY' && bareId(sub?.planAgencyId) ? 'AGENCY' : 'NATIONAL';

/** Rotulo curto da abrangencia: "Nacional" ou "Órgão: <nome>". */
export const planScopeLabel = (plan: ScopedPlan | null | undefined): string =>
    planScopeOf(plan) === 'AGENCY' ? `Órgão: ${plan?.agencyName || bareId(plan?.agencyId)}` : 'Nacional';

/** Assinatura ativa e dentro da vigencia (mesma regra do gate do backend). */
export const isSubscriptionCurrent = (sub: ScopedSubscription | null | undefined, now: number = Date.now()): boolean => {
    const status = String(sub?.status ?? '').toLowerCase();
    if (status !== 'active' && status !== 'trial') return false;
    if (sub?.active === false) return false;
    if (!sub?.endDate) return true;
    const end = new Date(sub.endDate).getTime();
    return Number.isFinite(end) ? end > now : true;
};

/** A assinatura libera licitacoes deste orgao? */
export const subscriptionCoversAgency = (sub: ScopedSubscription | null | undefined, agencyId: unknown): boolean => {
    if (subscriptionScopeOf(sub) === 'NATIONAL') return true;
    const agency = bareId(agencyId);
    return !!agency && agency === bareId(sub?.planAgencyId);
};

/** Primeira assinatura vigente que libera o orgao (ou null). */
export const findCoveringSubscription = <T extends ScopedSubscription>(subs: T[], agencyId: unknown, now: number = Date.now()): T | null =>
    (subs ?? []).find((s) => isSubscriptionCurrent(s, now) && subscriptionCoversAgency(s, agencyId)) ?? null;
